import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { KitchenStaffLayout } from '../../components/staff/KitchenStaffLayout';
import { ChefHat, Search, RefreshCw, AlertCircle, Loader2, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Bar Expeditor sees only Drinks; Kitchen Expeditor sees only Kitchen; Supervisor sees both.
const ROLE_TO_ORDER_TYPE = {
  'Bar Expeditor': 'Drink',
  'Kitchen Expeditor': 'Kitchen',
  'Supervisor': null,
};

export const MenuAvailability = () => {
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  // Per-item local edit state (for the portionsLeft input)
  const [portionInput, setPortionInput] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('userData');
    if (stored) {
      const parsed = JSON.parse(stored);
      setUser(parsed);
    }
  }, []);

  const fetchData = useCallback(async (propertyId) => {
    try {
      setLoading(true);
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/menu-items/${propertyId}`),
        axios.get(`${BACKEND_URL}/api/menu-categories/${propertyId}`),
      ]);
      if (itemsRes.data.success) setItems(itemsRes.data.items || []);
      if (catsRes.data.success) setCategories(catsRes.data.categories || []);
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'Failed to load menu items', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user?.entityId) fetchData(user.entityId);
  }, [user, fetchData]);

  const roleFilter = ROLE_TO_ORDER_TYPE[user?.roleName?.trim()] ?? null;

  const filteredItems = useMemo(() => {
    let list = items.filter(i => i.isActive !== false);
    if (roleFilter) list = list.filter(i => (i.orderType || 'Kitchen') === roleFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(i => (i.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [items, roleFilter, searchTerm]);

  const grouped = useMemo(() => {
    const byCat = {};
    for (const it of filteredItems) {
      const key = it.categoryId || 'uncategorised';
      if (!byCat[key]) byCat[key] = [];
      byCat[key].push(it);
    }
    return byCat;
  }, [filteredItems]);

  const catName = (id) => categories.find(c => c.id === id)?.name || 'Uncategorised';

  const stats = useMemo(() => {
    const total = filteredItems.length;
    const available = filteredItems.filter(i => i.isAvailable !== false).length;
    const limited = filteredItems.filter(i => typeof i.portionsLeft === 'number').length;
    return { total, available, unavailable: total - available, limited };
  }, [filteredItems]);

  const updateAvailability = async (item, isAvailable, portionsLeft) => {
    setSavingId(item.id);
    try {
      const body = { isAvailable };
      // portionsLeft is explicit: null/undefined => unlimited
      if (portionsLeft !== undefined) body.portionsLeft = portionsLeft;
      const res = await axios.put(`${BACKEND_URL}/api/menu-items/${item.id}/availability`, body);
      if (res.data.success) {
        setItems(prev => prev.map(i => i.id === item.id ? res.data.item : i));
        toast({
          title: 'Updated',
          description: `"${item.name}" is now ${isAvailable ? 'available' : 'unavailable'}${
            isAvailable && typeof portionsLeft === 'number' ? ` (${portionsLeft} portion(s) left)` : ''
          }`,
        });
      }
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'Failed to update item', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const onToggle = (item) => {
    // Toggling to available without clearing portionsLeft: keep existing portionsLeft.
    // Toggling to unavailable: just flip the flag, keep portionsLeft as-is.
    updateAvailability(item, !(item.isAvailable !== false), item.portionsLeft);
  };

  const onSetPortions = (item) => {
    const raw = portionInput[item.id];
    let portionsLeft = null;
    if (raw !== undefined && raw !== '') {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n) || n < 0) {
        toast({ title: 'Invalid', description: 'Portions must be 0 or a positive number', variant: 'destructive' });
        return;
      }
      portionsLeft = n;
    }
    // If portions set to 0, also mark unavailable; otherwise force available
    const isAvailable = portionsLeft === 0 ? false : true;
    updateAvailability(item, isAvailable, portionsLeft);
    setPortionInput(prev => ({ ...prev, [item.id]: '' }));
  };

  const onClearPortions = (item) => {
    updateAvailability(item, true, null);
    setPortionInput(prev => ({ ...prev, [item.id]: '' }));
  };

  if (loading) {
    return (
      <KitchenStaffLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-10 h-10 text-teal-500 animate-spin" />
        </div>
      </KitchenStaffLayout>
    );
  }

  const sectionLabel = roleFilter ? `${roleFilter === 'Drink' ? 'Bar' : 'Kitchen'} items` : 'All items';

  return (
    <KitchenStaffLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ChefHat className="w-6 h-6 text-teal-600" />
              Menu Availability
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {sectionLabel} · Toggle items on/off the live menu, or limit portions for the day.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => user?.entityId && fetchData(user.entityId)}
            data-testid="refresh-availability-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
            <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Available</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{stats.available}</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <p className="text-xs uppercase tracking-wider text-red-700 font-semibold">Unavailable</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{stats.unavailable}</p>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
            <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Limited Portions</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">{stats.limited}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search items by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="availability-search-input"
          />
        </div>

        {/* Item list grouped by category */}
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No items found</p>
            <p className="text-slate-400 text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'Items will appear here once added by an admin.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([catId, catItems]) => (
              <div key={catId} className="space-y-2">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                  {catName(catId)} · {catItems.length}
                </h2>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {catItems.map((item) => {
                    const isAvail = item.isAvailable !== false;
                    const hasLimit = typeof item.portionsLeft === 'number';
                    const saving = savingId === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`p-4 grid grid-cols-1 md:grid-cols-12 gap-3 md:items-center transition-colors ${isAvail ? '' : 'bg-red-50/30'}`}
                        data-testid={`avail-row-${item.id}`}
                      >
                        {/* Name + meta */}
                        <div className="md:col-span-5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold ${isAvail ? 'text-slate-900' : 'text-slate-500 line-through'}`}>
                              {item.name}
                            </p>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              (item.orderType || 'Kitchen') === 'Drink' ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {item.orderType || 'Kitchen'}
                            </span>
                            {!isAvail && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                Unavailable
                              </span>
                            )}
                            {hasLimit && isAvail && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                {item.portionsLeft} left
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.description}</p>
                          )}
                          <p className="text-xs text-slate-600 font-medium mt-0.5">${item.price?.toFixed(2)}</p>
                        </div>

                        {/* Portions input */}
                        <div className="md:col-span-4">
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              placeholder={hasLimit ? `Current: ${item.portionsLeft}` : 'Unlimited'}
                              value={portionInput[item.id] ?? ''}
                              onChange={(e) => setPortionInput(prev => ({ ...prev, [item.id]: e.target.value }))}
                              className="h-9 text-sm"
                              data-testid={`portions-input-${item.id}`}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-9 whitespace-nowrap"
                              onClick={() => onSetPortions(item)}
                              disabled={saving}
                              data-testid={`set-portions-btn-${item.id}`}
                            >
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Set'}
                            </Button>
                            {hasLimit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-9 text-slate-500"
                                onClick={() => onClearPortions(item)}
                                disabled={saving}
                                title="Clear limit (unlimited)"
                                data-testid={`clear-portions-btn-${item.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Toggle */}
                        <div className="md:col-span-3 flex md:justify-end">
                          <button
                            type="button"
                            onClick={() => onToggle(item)}
                            disabled={saving}
                            className={`relative inline-flex items-center h-9 w-36 rounded-full px-1 transition-colors ${
                              isAvail ? 'bg-emerald-500' : 'bg-slate-300'
                            } disabled:opacity-60`}
                            data-testid={`toggle-availability-${item.id}`}
                          >
                            <span
                              className={`inline-flex items-center justify-center h-7 w-7 rounded-full bg-white shadow transition-transform ${
                                isAvail ? 'translate-x-[6.75rem]' : 'translate-x-0'
                              }`}
                            >
                              {isAvail ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-slate-500" />}
                            </span>
                            <span className={`absolute text-xs font-semibold tracking-wide uppercase ${
                              isAvail ? 'left-3 text-white' : 'right-3 text-slate-600'
                            }`}>
                              {isAvail ? 'Available' : 'Unavailable'}
                            </span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </KitchenStaffLayout>
  );
};

export default MenuAvailability;
